import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { loadStripe } from '@stripe/stripe-js';
import {
  useStripe,
  useElements,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
} from '@stripe/react-stripe-js';
import { Button, StateAndCountrySelect, isLocationValid, EditInput } from '@piplapps/pipl-ui';
import AppConfig from '../../../Config/AppConfig';
import styles from './AddCreditCard.module.less';
import { BillingActions } from '../../../Redux/modules/billing/actions';
import { BillingSelectors } from '../../../Redux/modules/billing/selectors';
import { displayNotification } from '../../../Utils/GenericUtils';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler/AnalyticsHandler';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';

const AddCreditCard = (props) => {
  const [creditCardError, setCreditCardError] = useState(null);
  const [expiredCardError, setExpiredCardError] = useState(null);
  const [cvvError, setCvvError] = useState(null);
  const { data, isLoading } = props;
  const stripe = useStripe();
  const elements = useElements();
  const card = elements?.getElement(CardNumberElement);
  const validate = (values) => {
    const errors = {};

    for (const value of Object.keys(values)) {
      if (value !== 'cardNumber') {
        if (value !== 'state') {
          if (!values[value]) {
            errors[value] = gettext('Required');
          }
        }
      }
    }
    if (!isLocationValid(values.country_code, values.state).state) {
      errors.state = gettext('Required');
    }
    if (!isLocationValid(values.country_code, values.state).country) {
      errors.country_code = gettext('Required');
    }
    if (!values.cardNumber) {
      errors.cardNumber = gettext('Required');
    }
    if (!values.expirationDate) {
      errors.expirationDate = gettext('Required');
    }
    if (!values.cvv) {
      errors.cvv = gettext('Required');
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      name: data.name,
      cardNumber: false,
      expirationDate: false,
      cvv: false,
      address: data.address,
      country_code: data.country_code,
      state: data.state,
      city: data.city,
      postalCode: data.postal_code,
    },
    validate,
    onSubmit: async (values) => {
      try {
        props.connectingToStripe();
        const result = await stripe.createToken(card, {
          name: values.name,
          address_line1: values.address,
          address_city: values.city,
          address_state: values.state,
          address_zip: values.postalCode,
          address_country: values.country_code,
        });

        props.onSaveCard(result.token.id);
      } catch (e) {
        props.connectingToStripeFailed();
        displayNotification('error', 'Error', 'There was an error processing your credit card');
      }
    },
  });
  const stripeInputStyle = {
    base: {
      fontSize: '16px',
      color: '#152a4d',
      lineHeight: '42px',
      fontFamily: "'Open Sans', tahoma, arial, sans-serif",
      '::placeholder': {
        color: '#9b9b9b',
      },
      border: 'none',
    },
    invalid: {
      color: 'darkred',
    },
  };

  const setCountryValue = (value) => {
    if (value !== 'US' || value !== 'CA') {
      formik.setFieldValue('state', '');
    }
    formik.setFieldValue('country_code', value);
  };
  const setStateValue = (value) => formik.setFieldValue('state', value);

  return (
    <div>
      <form className={styles.formWrapper} onSubmit={formik.handleSubmit}>
        <EditInput
          name="name"
          id="cc-name"
          label={gettext('Name on Card')}
          onChange={formik.handleChange}
          value={formik.values.name}
          onBlur={formik.handleBlur}
          error={formik.touched.name && formik.errors.name ? formik.errors.name : null}
        />
        <div id="cc-number">
          <CardNumberElement
            onChange={(e) => {
              formik.setFieldValue('cardNumber', e.complete);
              setCreditCardError(null);

              if (e.error) {
                formik.setFieldError('cardNumber', e.error.message);
                setCreditCardError(e.error.message);
              }
            }}
            options={{ style: stripeInputStyle }}
          />
        </div>
        {creditCardError ? (
          <div className={styles.error}>{creditCardError}</div>
        ) : formik.errors.cardNumber && formik.touched.cardNumber ? (
          <div className={styles.error}>{formik.errors.cardNumber}</div>
        ) : null}
        <div className={styles.expirationCvvContainer}>
          <div className={styles.expirationCvv} id="exp-date">
            <CardExpiryElement
              options={{ style: stripeInputStyle }}
              onChange={(e) => {
                formik.setFieldValue('expirationDate', e.complete);
                setExpiredCardError(null);

                if (e.error) {
                  formik.setFieldError('expirationDate', e.error.message);
                  setExpiredCardError(e.error.message);
                }
              }}
            />
            {expiredCardError ? (
              <div className={styles.error}>{expiredCardError}</div>
            ) : formik.errors.expirationDate && formik.touched.expirationDate ? (
              <div className={styles.error}>{formik.errors.expirationDate}</div>
            ) : null}
          </div>

          <div className={styles.expirationCvv} id="cvc">
            <CardCvcElement
              options={{
                style: stripeInputStyle,
              }}
              onChange={(e) => {
                formik.setFieldValue('cvv', e.complete);
                setCvvError(null);

                if (e.error) {
                  formik.setFieldError('cvv', e.error.message);
                  setCvvError(e.error.message);
                }
              }}
            />
            {cvvError ? (
              <div className={styles.error}>{cvvError}</div>
            ) : formik.errors.cvv && formik.touched.cvv ? (
              <div className={styles.error}>{formik.errors.cvv}</div>
            ) : null}
          </div>
        </div>
        <div className={styles.text}>Make sure to enter the billing address associated with your card.</div>
        <EditInput
          name="address"
          id="cc-address"
          label={gettext('Address')}
          onChange={formik.handleChange}
          value={formik.values.address}
          onBlur={formik.handleBlur}
          error={formik.touched.address && formik.errors.address ? formik.errors.address : null}
        />
        <StateAndCountrySelect
          onBlur={formik.handleBlur}
          countryError={formik.errors.country_code && formik.touched.country_code ? formik.errors.country_code : null}
          stateError={formik.errors.state && formik.touched.state ? formik.errors.state : null}
          setCountryValue={setCountryValue}
          setStateValue={setStateValue}
          stateValue={formik.values.state}
          countryValue={formik.values.country_code}
        />
        <EditInput
          name="city"
          id="cc-city"
          label={gettext('City')}
          onChange={formik.handleChange}
          value={formik.values.city}
          onBlur={formik.handleBlur}
          error={formik.touched.city && formik.errors.city ? formik.errors.city : null}
        />
        <EditInput
          name="postalCode"
          id="cc-zip"
          label={gettext('Postal Code')}
          onChange={formik.handleChange}
          value={formik.values.postalCode}
          onBlur={formik.handleBlur}
          error={formik.touched.postalCode && formik.errors.postalCode ? formik.errors.postalCode : null}
        />
        {Object.keys(formik.errors).length > 0 && Object.keys(formik.touched).length > 8 ? (
          <div className={styles.redText}>{gettext('Your form has a problem.. please revise and try again')}</div>
        ) : null}
        <div className={styles.buttonsContainer}>
          <div className={styles.buttonUpdateWrapper}>
            <AnalyticsHandler
              eventName={AnalyticsEventNames.SAVE_CREDIT_CARD}
              eventData={{ updateCreditCard: !!props.data.name }}
            >
              <Button
                id="submit-button"
                buttonClass="primary-blue"
                type="submit"
                text={gettext('Save')}
                isLoading={isLoading}
                disabled={!formik.isValid || formik.initialValues === formik.values}
              />
            </AnalyticsHandler>
          </div>
          <div className={styles.buttonCancelWrapper}>
            <Button
              id="cancel-button"
              buttonClass="secondary-blue"
              text={gettext('Cancel')}
              onClick={() => props.toggleBillingEditMode()}
              type="button"
            />
          </div>
        </div>
      </form>
    </div>
  );
};

const mapDispatchToProps = {
  onSaveCard: BillingActions.addCreditCard,
  connectingToStripe: BillingActions.connectingToStripe,
  connectingToStripeFailed: BillingActions.connectingToStripeFailed,
};

const mapStateToProps = (state) => {
  return {
    isLoading: BillingSelectors.editingCardLoadingSelector(state),
    billingEditMode: BillingSelectors.billingEditModeSelector(state),
  };
};

AddCreditCard.propTypes = {
  data: PropTypes.object,
  onSaveCard: PropTypes.func,
  connectingToStripe: PropTypes.func,
  connectingToStripeFailed: PropTypes.func,
  isLoading: PropTypes.bool,
  toggleBillingEditMode: PropTypes.func,
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const StripeForm = withConnect(AddCreditCard);
const stripePromise = loadStripe(AppConfig.STRIPE_PUBLIC_KEY);

export default (props) => {
  return (
    <Elements fonts={[{ cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans' }]} stripe={stripePromise}>
      <StripeForm {...props} />
    </Elements>
  );
};
